import React, { forwardRef } from "react";
import moment from "moment";
import ImageTag from "../../reusebale/imageTag";

const TransactionInvoiceTemplate = forwardRef(({ transaction, leaseDetails, landlordInfo }, ref) => {
  // Format currency helper
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Get tenant information
  const getTenantInfo = () => {
    if (!leaseDetails || !leaseDetails.tenantInfo) {
      return {
        name: "N/A",
        email: "N/A",
        phone: "N/A"
      };
    }
    return {
      name: `${leaseDetails.tenantInfo.firstName || ""} ${leaseDetails.tenantInfo.lastName || ""}`.trim(),
      email: leaseDetails.tenantInfo.email || "N/A",
      phone: leaseDetails.tenantInfo.phoneNumber || "N/A"
    };
  };

  // Get property information
  const getPropertyInfo = () => {
    if (!leaseDetails || !leaseDetails.propertyInfo) {
      return {
        name: "N/A",
        address: "N/A",
        unit: "N/A"
      };
    }
    return {
      name: leaseDetails.propertyInfo.propertyName || "N/A",
      address: leaseDetails.propertyInfo.completeAddress 
        || leaseDetails.propertyInfo.address 
        || "N/A",
      unit: leaseDetails.propertyInfo.unitNumber || leaseDetails.unit || "N/A"
    };
  };

  const tenantInfo = getTenantInfo();
  const propertyInfo = getPropertyInfo();
  
  // Calculate amounts
  const rentAmount = transaction.amount - (transaction.security_deposit || 0) - (transaction.security_deposit_refund || 0);
  const securityDeposit = transaction.security_deposit || 0;
  const securityDepositRefund = transaction.security_deposit_refund || 0;
  const totalAmount = transaction.amount;

  // Determine transaction type
  const getTransactionType = () => {
    const isDebit = transaction.sourceAccountType === "internal" && transaction.destinationAccountType === "external";
    const isCredit = transaction.sourceAccountType === "external" && transaction.destinationAccountType === "internal";
    return isDebit ? "Debit" : isCredit ? "Credit" : "Transfer";
  };

  const transactionType = getTransactionType();

  return (
    <div ref={ref} className="w-[8.27in] h-[11.69in] mx-auto p-8 bg-white">
      {/* Header */}
      <div className="flex justify-between items-start mb-8">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">RENT INVOICE</h1>
          <div className="text-sm text-gray-600">
            <p className="font-semibold">
              Invoice # {`INV-${transaction._id?.slice(-8) || "UNKNOWN"}`}
            </p>
            <p>Date: {moment(transaction.createdAt).format("MM/DD/YYYY")}</p>
          </div>
        </div>

        <div className="text-right">
          <ImageTag
            path="/assets/logo.png"
            classes="w-full h-32"
            altText="logo"
          />
        </div>
      </div>

      {/* From/To Section */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Bill To:</h3>
          <div className="text-sm text-gray-700">
            <p className="font-semibold">{landlordInfo.firstName} {landlordInfo.lastName}</p>
            <p>{landlordInfo.email}</p>
            {landlordInfo.phoneNumber && (
              <p>Phone: {landlordInfo.phoneNumber}</p>
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Bill From:</h3>
          <div className="text-sm text-gray-700">
            <p className="font-semibold">{tenantInfo.name}</p>
            <p>Email: {tenantInfo.email}</p>
            <p>Phone: {tenantInfo.phone}</p>
          </div>
        </div>
      </div>

      {/* Property Details */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Property Details</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p><span className="font-medium">Property Name:</span> {propertyInfo.name}</p>
              <p><span className="font-medium">Property Address:</span> {propertyInfo.address}</p>
              <p><span className="font-medium">Unit:</span> {propertyInfo.unit}</p>
            </div>
            <div>
              <p><span className="font-medium">Lease Type:</span> {leaseDetails?.leaseType || "N/A"}</p>
              <p><span className="font-medium">Lease:</span> {transaction.leaseId ? `LES-${transaction.leaseId.slice(0, 10)}` : "N/A"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Items Table */}
      <div className="mb-8">
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 p-3 text-left text-sm font-semibold">
                Description
              </th>
              <th className="border border-gray-300 p-3 text-center text-sm font-semibold">
                Type
              </th>
              <th className="border border-gray-300 p-3 text-right text-sm font-semibold">
                Amount
              </th>
            </tr>
          </thead>
          <tbody>
            {rentAmount > 0 && (
              <tr>
                <td className="border border-gray-300 p-3 text-sm">
                  <div>
                    <p className="font-medium">Rent Payment</p>
                    <p className="text-gray-600 text-xs">
                      Monthly rent payment for {propertyInfo.name}
                    </p>
                  </div>
                </td>
                <td className="border border-gray-300 p-3 text-center text-sm">
                  Rent
                </td>
                <td className="border border-gray-300 p-3 text-right text-sm font-medium">
                  {formatCurrency(rentAmount)}
                </td>
              </tr>
            )}
            {securityDeposit > 0 && (
              <tr>
                <td className="border border-gray-300 p-3 text-sm">
                  <div>
                    <p className="font-medium">Security Deposit</p>
                    <p className="text-gray-600 text-xs">
                      Security deposit for lease agreement
                    </p>
                  </div>
                </td>
                <td className="border border-gray-300 p-3 text-center text-sm">
                  Deposit
                </td>
                <td className="border border-gray-300 p-3 text-right text-sm font-medium">
                  {formatCurrency(securityDeposit)}
                </td>
              </tr>
            )}
            {securityDepositRefund > 0 && (
              <tr>
                <td className="border border-gray-300 p-3 text-sm">
                  <div>
                    <p className="font-medium">Security Deposit Refund</p>
                    <p className="text-green-600 text-xs font-semibold">
                      REFUNDED - End of lease term
                    </p>
                  </div>
                </td>
                <td className="border border-gray-300 p-3 text-center text-sm">
                  Refund
                </td>
                <td className="border border-gray-300 p-3 text-right text-sm font-medium text-green-600">
                  {formatCurrency(securityDepositRefund)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Total Section */}
      <div className="flex justify-end mb-8">
        <div className="w-64">
          <div className="border-t border-gray-300 mt-2 pt-2">
            <div className="flex justify-between py-2 text-lg font-bold">
              <span>Total Amount:</span>
              <span>{formatCurrency(totalAmount)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-gray-300">
        <div className="text-center text-xs text-gray-600">
          <p className="font-semibold mb-1">FlexManagement - Property Management System</p>
          <p>Thank you for your business!</p>
          <p className="mt-2">For any questions, please contact us at support@flexmanagement.com</p>
        </div>
      </div>
    </div>
  );
});

export default TransactionInvoiceTemplate;
